/**
 * FeatureFlagsPanel — READ-ONLY view of the platform's feature flags
 * (Settings → Admin → Feature Flags).
 *
 * Flags are resolved server-side from the runtime env, so when this hits the DEPLOYED
 * backend it shows the REAL production state (the Railway env values a code checkout
 * can't see) — the point of this panel: see what's on in prod without SSH-ing Railway.
 * Intentionally read-only: toggling stays in Railway (no runtime write endpoint exists,
 * and turning a prod-config writer loose from the app would be a footgun on a legal system).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Flag } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';

type DetailedFlag = { enabled?: boolean; description?: string } | boolean;

export default function FeatureFlagsPanel() {
  const { getToken } = useAuth();
  const [flags, setFlags] = useState<Record<string, DetailedFlag>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const data = await api.get<Record<string, DetailedFlag>>('/api/v1/feature-flags/detailed', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setFlags(data || {});
      } catch (e: any) {
        if (active) setError(e?.message || 'Could not load feature flags.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [getToken]);

  const rows = useMemo(() => {
    return Object.entries(flags)
      .map(([name, v]) => ({
        name,
        enabled: typeof v === 'boolean' ? v : !!v?.enabled,
        description: typeof v === 'boolean' ? '' : v?.description || '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [flags]);

  const enabledCount = rows.filter((r) => r.enabled).length;

  return (
    <div className="space-y-6">
      {/* Plain section header — no orange fill (K3 gate: orange reserved for nav + primary action). */}
      <div className="flex items-center gap-3">
        <Flag className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        <div>
          <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Feature Flags</h2>
          <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>
            Live runtime state — read-only (toggled via Railway env in production)
          </p>
        </div>
      </div>

      <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm" style={{ color: 'var(--color-status-adverse)' }}>{error}</p>
        ) : rows.length === 0 ? (
          <p className="py-12 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>No flags reported.</p>
        ) : (
          <>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
              <strong style={{ color: 'var(--color-text-primary)' }}>{enabledCount}</strong> of{' '}
              <strong style={{ color: 'var(--color-text-primary)' }}>{rows.length}</strong> enabled
            </p>
            <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
              {rows.map((r) => (
                <div key={r.name} className="flex items-start justify-between gap-4 py-3"
                  style={{ borderColor: 'var(--color-border)' }}>
                  <div className="min-w-0">
                    <p className="font-mono text-sm" style={{ color: 'var(--color-text-primary)' }}>{r.name}</p>
                    {r.description && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{r.description}</p>
                    )}
                  </div>
                  <span
                    className="shrink-0 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-mono uppercase tracking-wide"
                    style={{
                      color: r.enabled ? 'var(--color-verified)' : 'var(--color-text-dim)',
                      borderColor: r.enabled ? 'var(--color-verified)' : 'var(--color-border)',
                    }}
                  >
                    {r.enabled ? 'On' : 'Off'}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
